/**
 * LoadModal
 *
 * Styled confirmation modal shown before loading an estimate file.
 * Displays a summary of the file contents and asks the user to confirm.
 */

import React, { useEffect } from 'react';
import './LoadModal.css';

export interface LoadConfirmInfo {
  projectName: string;
  mode: string;
  dimensions: string;
  stepsComplete: number;
}

interface LoadModalProps {
  open: boolean;
  info: LoadConfirmInfo | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const LoadModal: React.FC<LoadModalProps> = ({ open, info, onConfirm, onCancel }) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open || !info) return null;

  const modeLabels: Record<string, string> = {
    FullRoom: 'Full Room',
    CeilingOnly: 'Ceiling Only',
    IndividualWalls: 'Individual Walls',
  };

  return (
    <div className="load-modal-overlay" onClick={onCancel}>
      <div className="load-modal" onClick={e => e.stopPropagation()}>
        <div className="load-modal-header">
          <h5 className="load-modal-title">📂 Load Estimate</h5>
          <button className="load-modal-close" onClick={onCancel} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="load-modal-body">
          <p className="load-modal-question">Load this estimate?</p>
          <div className="load-modal-summary">
            <div className="load-modal-row">
              <span className="load-modal-label">Project</span>
              <span className="load-modal-value">{info.projectName}</span>
            </div>
            <div className="load-modal-row">
              <span className="load-modal-label">Mode</span>
              <span className="load-modal-value">{modeLabels[info.mode] || info.mode}</span>
            </div>
            <div className="load-modal-row">
              <span className="load-modal-label">Dimensions</span>
              <span className="load-modal-value">{info.dimensions}</span>
            </div>
            <div className="load-modal-row">
              <span className="load-modal-label">Steps done</span>
              <span className="load-modal-value">{info.stepsComplete}</span>
            </div>
          </div>
          <p className="load-modal-warning">
            This will replace all current data in the estimator.
          </p>
        </div>
        <div className="load-modal-footer">
          <button type="button" className="load-modal-btn load-modal-btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="load-modal-btn load-modal-btn--confirm" onClick={onConfirm}>
            Load Estimate
          </button>
        </div>
      </div>
    </div>
  );
};

